/**
 * Fields — constructeurs de champs de formulaire réutilables.
 * Chaque constructeur renvoie un handle { element, getValue, setValue }.
 */
import { h } from "./dom.js";
import { icon } from "./icons.js";

export interface FieldHandle<T> {
	element: HTMLElement;
	getValue(): T;
	setValue(value: T): void;
}

export interface SliderOptions {
	min: number;
	max: number;
	step?: number;
	value: number;
	label?: string;
	format?: (value: number) => string;
	onInput?: (value: number) => void;
}

/** Slider + badge de valeur. */
export function sliderField(options: SliderOptions): FieldHandle<number> {
	const format = options.format ?? ((v: number) => String(v));
	const input = h("input", {
		type: "range",
		min: options.min,
		max: options.max,
		step: options.step ?? 1,
	}) as HTMLInputElement;
	const badge = h("span", { class: "value-badge" }, format(options.value));
	input.value = String(options.value);
	input.addEventListener("input", () => {
		const value = Number(input.value);
		badge.textContent = format(value);
		options.onInput?.(value);
	});
	const element = h(
		"div",
		{ class: "form-group" },
		options.label ? h("label", { class: "field-label" }, options.label) : null,
		h("div", { class: "range-with-value" }, input, badge)
	);
	return {
		element,
		getValue: () => Number(input.value),
		setValue: (v) => {
			input.value = String(v);
			badge.textContent = format(v);
		},
	};
}

export interface SelectOptions<T extends string> {
	label?: string;
	options: { value: T; label: string }[];
	value: T;
	onChange?: (value: T) => void;
}

export function selectField<T extends string>(options: SelectOptions<T>): FieldHandle<T> {
	const select = h(
		"select",
		null,
		...options.options.map((opt) => h("option", { value: opt.value }, opt.label))
	) as HTMLSelectElement;
	select.value = options.value;
	select.addEventListener("change", () => options.onChange?.(select.value as T));
	const element = h(
		"div",
		{ class: "form-group" },
		options.label ? h("label", { class: "field-label" }, options.label) : null,
		select
	);
	return {
		element,
		getValue: () => select.value as T,
		setValue: (v) => {
			select.value = v;
		},
	};
}

export interface TextAreaOptions {
	label?: string;
	placeholder?: string;
	value?: string;
	rows?: number;
	maxLength?: number;
	onInput?: (value: string) => void;
}

export function textAreaField(options: TextAreaOptions): FieldHandle<string> {
	const textarea = h("textarea", {
		placeholder: options.placeholder,
		rows: options.rows ?? 3,
		maxLength: options.maxLength,
	}) as HTMLTextAreaElement;
	textarea.value = options.value ?? "";
	textarea.addEventListener("input", () => options.onInput?.(textarea.value));
	const element = h(
		"div",
		{ class: "form-group" },
		options.label ? h("label", { class: "field-label" }, options.label) : null,
		textarea
	);
	return {
		element,
		getValue: () => textarea.value,
		setValue: (v) => {
			textarea.value = v;
		},
	};
}

export interface CheckboxOptions {
	label: string;
	checked?: boolean;
	onChange?: (checked: boolean) => void;
}

export function checkboxField(options: CheckboxOptions): FieldHandle<boolean> {
	const input = h("input", { type: "checkbox" }) as HTMLInputElement;
	input.checked = options.checked ?? false;
	input.addEventListener("change", () => options.onChange?.(input.checked));
	const element = h(
		"div",
		{ class: "form-group" },
		h("label", { class: "checkbox-label" }, input, options.label)
	);
	return {
		element,
		getValue: () => input.checked,
		setValue: (v) => {
			input.checked = v;
		},
	};
}

export interface NumberOptions {
	label?: string;
	value: number;
	min?: number;
	max?: number;
	step?: number;
	placeholder?: string;
	onInput?: (value: number) => void;
}

export function numberField(options: NumberOptions): FieldHandle<number> {
	const input = h("input", {
		type: "number",
		value: options.value,
		min: options.min,
		max: options.max,
		step: options.step,
		placeholder: options.placeholder,
	}) as HTMLInputElement;
	input.addEventListener("input", () => options.onInput?.(Number(input.value)));
	const element = h(
		"div",
		{ class: "form-group" },
		options.label ? h("label", { class: "field-label" }, options.label) : null,
		input
	);
	return {
		element,
		getValue: () => Number(input.value),
		setValue: (v) => {
			input.value = String(v);
		},
	};
}

/**
 * Champ seed avec bouton « au hasard » (-1 = aléatoire).
 */
export function seedField(initial = -1): FieldHandle<number> {
	const input = h("input", {
		type: "number",
		value: initial,
		step: 1,
		title: "-1 ou vide = au hasard",
	}) as HTMLInputElement;
	const dice = h(
		"button",
		{
			class: "btn btn-small",
			type: "button",
			title: "Tirer une graine au hasard",
			"aria-label": "Tirer une graine au hasard",
			onclick: () => {
				input.value = String(Math.floor(Math.random() * 2 ** 31));
			},
		},
		icon("shuffle")
	);
	const element = h(
		"div",
		{ class: "form-group" },
		h("label", { class: "field-label" }, "Graine (la même graine refait la même image)"),
		h("div", { class: "range-with-value" }, input, dice)
	);
	return {
		element,
		getValue: () => (input.value === "" ? -1 : Number(input.value)),
		setValue: (v) => {
			input.value = String(v);
		},
	};
}

export interface TextInputOptions {
	label?: string;
	placeholder?: string;
	value?: string;
	type?: "text" | "email" | "password" | "url";
	maxLength?: number;
	onInput?: (value: string) => void;
}

export function textInputField(options: TextInputOptions): FieldHandle<string> {
	const input = h("input", {
		type: options.type ?? "text",
		placeholder: options.placeholder,
		maxLength: options.maxLength,
	}) as HTMLInputElement;
	input.value = options.value ?? "";
	input.addEventListener("input", () => options.onInput?.(input.value));
	const element = h(
		"div",
		{ class: "form-group" },
		options.label ? h("label", { class: "field-label" }, options.label) : null,
		input
	);
	return {
		element,
		getValue: () => input.value,
		setValue: (v) => {
			input.value = v;
		},
	};
}
